import { useEffect, useState } from 'react';
import { CheckCircle2, Circle, Loader2, Search, Sparkles, XCircle } from 'lucide-react';
import Button from '../ui/Button';
import { Card, CardContent } from '../ui/Card';
import pestModelService, { type LookupStep, type LookupStepState } from '../../services/pestModels';
import { errorMessage } from '../../utils/apiResponse';
import type { PestLookupResult } from '../../types/pest';

type Found = Extract<PestLookupResult, { identified: true }>;

// The research runs as separate requests, so the checklist shows what has
// actually finished rather than a guess on a timer. The last two run together.
const STEPS: Array<{ key: LookupStep; label: string }> = [
  { key: 'identify', label: 'Identifying the pest and its host crops' },
  { key: 'model', label: 'Researching its forecast model' },
  { key: 'guide', label: 'Writing its field guide' },
];

/**
 * "What are you dealing with?" — the grower describes the pest in their own
 * words and the server researches it. The result is handed back as a proposal
 * to review in the form; nothing is saved here.
 */
export default function PestLookupPanel({ initialQuery = '', onFound, onManual, onCancel, title }: {
  initialQuery?: string;
  onFound: (proposal: Found) => void;
  /** Offered as the way out when the grower would rather type it in. */
  onManual?: () => void;
  onCancel?: () => void;
  title?: string;
}) {
  const [query, setQuery] = useState(initialQuery);
  const [crop, setCrop] = useState('');
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState<{ message: string; candidates: string[] } | null>(null);
  const [steps, setSteps] = useState<Partial<Record<LookupStep, { state: LookupStepState; detail?: string }>>>({});

  useEffect(() => {
    if (!busy) return;
    setElapsed(0);
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [busy]);

  const run = async (q = query) => {
    if (q.trim().length < 2) return;
    setQuery(q);
    setBusy(true);
    setError(null);
    setNotFound(null);
    setSteps({});
    try {
      const r = await pestModelService.lookup(q.trim(), crop.trim() || null, (p) =>
        setSteps((prev) => ({ ...prev, [p.step]: { state: p.state, detail: p.detail } })));
      if (r.identified) onFound(r);
      else setNotFound({ message: r.message, candidates: r.candidates });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-5 space-y-4">
        <div>
          <h2 className="text-base font-semibold text-neutral-900 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary-600" /> {title ?? 'What are you dealing with?'}
          </h2>
          <p className="text-sm text-neutral-500 mt-1">
            Describe it however you know it — a common name, what it does to the fruit or leaves, or the scientific name if you have it.
            We'll identify it, research it, and set up its forecast. You review everything before it's saved.
          </p>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); run(); }} className="space-y-3">
          <label className="block">
            <span className="text-xs font-medium text-neutral-600">Pest or disease</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              disabled={busy}
              autoFocus
              placeholder="e.g. brown rot on the plums, light brown apple moth, powdery mildew"
              className="mt-1 w-full border border-neutral-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-neutral-50"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-neutral-600">Seen on (optional)</span>
            <input
              value={crop}
              onChange={(e) => setCrop(e.target.value)}
              disabled={busy}
              placeholder="e.g. apples, cherries, table grapes"
              className="mt-1 w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-neutral-50"
            />
          </label>

          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" variant="primary" disabled={busy || query.trim().length < 2}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              {busy ? 'Researching…' : 'Research it'}
            </Button>
            {onManual && !busy && <Button type="button" variant="ghost" onClick={onManual}>Enter details by hand</Button>}
            {onCancel && !busy && <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>}
          </div>
        </form>

        {busy && (
          <div className="rounded-lg bg-primary-50 border border-primary-100 px-3 py-2.5 text-sm text-primary-900 space-y-1.5" role="status">
            {STEPS.map(({ key, label }) => {
              const s = steps[key];
              return (
                <div key={key} className="flex items-start gap-2 min-w-0">
                  <span className="mt-0.5 flex-shrink-0">
                    {s?.state === 'done' ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      : s?.state === 'failed' ? <XCircle className="h-4 w-4 text-red-600" />
                      : s?.state === 'running' ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Circle className="h-4 w-4 text-primary-300" />}
                  </span>
                  <div className="min-w-0">
                    <div className={s ? '' : 'text-primary-900/50'}>{label}</div>
                    {s?.detail && <div className={`text-xs break-words ${s.state === 'failed' ? 'text-red-700' : 'text-primary-700/80'}`}>{s.detail}</div>}
                  </div>
                </div>
              );
            })}
            <div className="text-xs text-primary-700/80 pt-0.5">{elapsed}s — the research steps usually take one to three minutes.</div>
          </div>
        )}

        {error && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">{error}</div>}

        {notFound && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-sm text-amber-900 space-y-2">
            <p>{notFound.message}</p>
            {notFound.candidates.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs">Did you mean:</span>
                {notFound.candidates.map((c) => (
                  <button key={c} onClick={() => run(c)} className="text-xs px-2 py-1 rounded-full bg-white border border-amber-300 hover:bg-amber-100">{c}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export type { Found as PestLookupFound };
